/**
 * Skillsnap TypeScript Types
 * Requirements: Data Models from design.md
 */

// Job Phases
export type JobPhase = 
  | 'Queued' | 'Generating' | 'Ready'
  | 'Applied' | 'Follow-Up' | 'Negotiation' | 'Accepted'
  | 'Skipped' | 'Expired' | 'Errored' | 'Trash';

export const VALID_PHASES: JobPhase[] = [
  'Queued', 'Generating', 'Ready',
  'Applied', 'Follow-Up', 'Negotiation', 'Accepted',
  'Skipped', 'Expired', 'Errored', 'Trash'
];

export const ACTIVE_PHASES: JobPhase[] = [
  'Queued', 'Generating', 'Ready',
  'Applied', 'Follow-Up', 'Negotiation'
];

// Generation States
export type GenerationState = 'locked' | 'ready' | 'generating' | 'complete' | 'error';

// Generation Types
export type GenerationType = 'manual' | 'ai' | 'ai_rewrite' | 'omit';

// Subcomponent Types
export type SubcomponentType =
  | 'contact' | 'summary' | 'skills' | 'highlights'
  | 'experience' | 'education' | 'awards' | 'presentations'
  | 'publications' | 'portfolios' | 'coverletter';

export const SUBCOMPONENTS: SubcomponentType[] = [
  'contact', 'summary', 'highlights', 'skills',
  'experience', 'education', 'awards', 'presentations',
  'publications', 'portfolios', 'coverletter'
];

// Default section order for resume rendering (excludes coverletter which is separate)
export const DEFAULT_SECTION_ORDER: SubcomponentType[] = [
  'contact', 'summary', 'highlights', 'skills',
  'experience', 'education', 'awards', 'presentations',
  'publications', 'portfolios'
];

// Generation type restrictions per component
// Components not listed here allow all types: manual, ai, ai_rewrite, omit
export const GENERATION_TYPE_RESTRICTIONS: Partial<Record<SubcomponentType, GenerationType[]>> = {
  contact: ['manual', 'omit', 'ai'],
  summary: ['ai_rewrite', 'manual', 'omit'],
  // skills: all types allowed (uses default)
  highlights: ['ai_rewrite', 'omit'],
  // experience: all types allowed (uses default)
  education: ['manual', 'ai', 'omit'],
  awards: ['ai', 'manual', 'omit'],
  presentations: ['ai', 'manual', 'omit'],
  publications: ['ai', 'manual', 'omit'],
  portfolios: ['ai', 'manual', 'omit'],
  coverletter: ['ai_rewrite', 'omit'],
};

// Contact Item (for list of contact methods)
export interface ContactItem {
  icon: 'email-at' | 'phone' | 'phone-volume' | 'linkedin' | 'x-twitter' | 'bluesky' | 'github' | 'github-square' | 'globe' | 'house' | 'facebook' | 'discord' | 'slack' | 'telegram' | 'whatsapp' | 'signal-chat';
  title: string;
  url: string;
}

// Contact Info
export interface Contact {
  items: ContactItem[];
}

// Experience Bullet
export interface ExperienceBullet {
  text: string;
  tags: string[];
}

// Experience Role
export interface ExperienceRole {
  title: string;
  startDate: string;
  endDate?: string;
  current: boolean;
  location?: string;
  bullets: ExperienceBullet[];
}

// Experience Company
export interface ExperienceCompany {
  name: string;
  url?: string;
  employees?: string;
  startDate: string;
  endDate?: string;
  current: boolean;
  location?: string;
  description?: string;
  roles: ExperienceRole[];
}

// Legacy Experience Entry (for backward compatibility)
export interface Experience {
  company: string;
  title: string;
  startDate: string;
  endDate?: string;
  current: boolean;
  description: string;
  achievements: string[];
}

// Education Entry
export interface Education {
  institution: string;
  degree: string;
  field: string;
  graduationDate: string;
  gpa?: string;
}

// Award Entry
export interface Award {
  title: string;
  issuer: string;
  date: string;
  description?: string;
}

// Presentation Entry (formerly Keynote)
export interface Presentation {
  title: string;
  type?: string;
  event: string;
  date: string;
  location?: string;
}

// Publication Entry
export interface Publication {
  title: string;
  url?: string;
  dates: string;
}

// Portfolio Entry
export interface Portfolio {
  title: string;
  url?: string;
  dates: string;
}

// Recommendation from AI Resume Review
export interface Recommendation {
  id: string;
  path: string;
  field: string;
  desc: string;
  replacement: Record<string, unknown>;
  status: 'pending' | 'accepted' | 'dismissed';
  created_at: string;
}

// Resume JSON
export interface ResumeJSON {
  name: string;
  contact: Contact;
  summary: string;
  skills: string[];
  highlights: string[];
  experience: Experience[] | ExperienceCompany[];
  education: Education[];
  awards: Award[];
  presentations: Presentation[];
  publications: Publication[];
  portfolios: Portfolio[];
  recommendations?: Recommendation[];
}

// Resume
export interface Resume {
  userid: string;
  resumename: string;
  resumejson: ResumeJSON;
  lastupdate: string;
}

// Job
export interface Job {
  jobid: string;
  postedts: string;
  jobcompany: string;
  joblistid?: string;
  jobtitle: string;
  jobtitlesafe: string;
  jobdesc: string;
  joblocation?: string;
  jobsalary?: string;
  jobposteddate: string;
  joburl?: string;
  jobcompanylogo?: string;
  jobtags: string[];
  createdAt: string;
}

// User Job (combined with Job for display)
export interface UserJob extends Job {
  userid: string;
  resumeid?: string;
  jobphase: JobPhase;
  
  // Subcomponent data
  datacontact?: string;
  datasummary?: string;
  dataskills?: string;
  datahighlights?: string;
  dataexperience?: string;
  dataeducation?: string;
  dataawards?: string;
  datapresentations?: string;
  datapublications?: string;
  dataportfolios?: string;
  datacoverletter?: string;
  
  // Generation states
  statecontact: GenerationState;
  statesummary: GenerationState;
  stateskills: GenerationState;
  statehighlights: GenerationState;
  stateexperience: GenerationState;
  stateeducation: GenerationState;
  stateawards: GenerationState;
  statepresentations: GenerationState;
  statepublications: GenerationState;
  stateportfolios: GenerationState;
  statecoverletter: GenerationState;
  
  // Generation types
  typecontact: GenerationType;
  typesummary: GenerationType;
  typeskills: GenerationType;
  typehighlights: GenerationType;
  typeexperience: GenerationType;
  typeeducation: GenerationType;
  typeawards: GenerationType;
  typepresentations: GenerationType;
  typepublications: GenerationType;
  typeportfolios: GenerationType;
  typecoverletter: GenerationType;
  
  // Final file locations
  s3locresumehtml?: string;
  s3locresumepdf?: string;
  s3loccoverletterhtml?: string;
  s3loccoverletterpdf?: string;
  s3locresumetxt?: string;
  s3loccoverlettertxt?: string;
  s3locresumetemplate?: string;

  // Feedback on generated files
  feedback_resume?: 'U' | 'D';
  feedback_coverletter?: 'U' | 'D';

  // Calculated fields
  postingAge?: number;
  updatedAt: string;
}

// --- New orthogonal user model ---
// Role: permissions/access level
export type Role = 'user' | 'admin' | 'snappy' | 'test';

// Status: feature gating
export type Status = 'trial' | 'active' | 'snoozing' | 'cancelled' | 'expired';

// Plan: billing model
export type Plan = 'free' | 'consumer' | 'enterprise';

// Legacy types (kept for backward compat during migration)
export type UserType = Role | 'trial' | 'Trial' | 'User' | 'Admin' | 'Snappy' | 'Test';
export type LegacyUserType =
  | 'user-active' | 'user-acct' | 'user-trial' | 'user-paused'
  | 'test-active' | 'test-trial' | 'test-paused'
  | 'admin-skillsnap' | 'admin-acct';
export type SubType = 'standard' | 'enterprise' | Plan;
export type SubStatus = Status | 'sleeping';
export type SubFrequency = 'monthly' | 'annual' | 'one-time' | 'lifetime' | 'other';
export type BillingType = string;
export type AccountType = BillingType;
export type SubscriptionStatus = 'active' | 'paused' | 'cancelled' | 'none';

// User Preferences
export interface UserPreferences {
  default_gen_contact: GenerationType;
  default_gen_summary: GenerationType;
  default_gen_skills: GenerationType;
  default_gen_highlights: GenerationType;
  default_gen_experience: GenerationType;
  default_gen_education: GenerationType;
  default_gen_awards: GenerationType;
  default_gen_presentations: GenerationType;
  default_gen_publications: GenerationType;
  default_gen_portfolios: GenerationType;
  default_gen_coverletter: GenerationType;
  // Resume Generation Options
  show_year_education?: boolean;
  show_year_awards?: boolean;
  show_year_presentations?: boolean;
  show_year_publications?: boolean;
  show_year_portfolios?: boolean;
  cutoff_year?: number;
  tutorial_completed?: boolean;
  marketing_email_optin?: boolean;
  section_names?: string;
  section_order?: string;
  admin_timezone?: string;
  // App Options - Active Phases (Days before moving to Expired)
  days_queued_before_expired?: number;
  days_generating_before_expired?: number;
  days_ready_before_expired?: number;
  days_applied_before_expired?: number;
  days_followup_before_expired?: number;
  days_negotiation_before_expired?: number;
  days_accepted_before_expired?: number;
  // App Options - Terminal Phases (Days before Trash/Delete)
  days_skipped_before_trashed?: number;
  days_errored_before_trashed?: number;
  days_expired_before_trashed?: number;
  days_trash_before_deleted?: number;
  // New orthogonal user model
  role?: Role;
  status?: Status;
  plan?: Plan;
  stripe_customer_id?: string;
  org_id?: string;
  org_name?: string;
  // Usage tracking
  jobs_created_this_month?: number;
  monthly_job_limit?: number;
  // Legacy aliases (backward compat)
  user_type?: UserType | LegacyUserType;
  sub_type?: SubType;
  sub_status?: SubStatus;
}

// API Response Types
export interface ApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
}

export interface JobListResponse {
  jobs: UserJob[];
  count: number;
  phaseCounts: Record<JobPhase, number>;
  totalCount: number;
}

export interface ResumeListResponse {
  resumes: Resume[];
  count: number;
}

export interface GenerationStatusResponse {
  jobid: string;
  phase: JobPhase;
  overallStatus: 'ready' | 'generating' | 'complete' | 'error';
  subcomponents: Record<SubcomponentType, {
    state: GenerationState;
    type: GenerationType;
    hasContent: boolean;
  }>;
  finalFiles: {
    resumeHtml: { ready: boolean; generated: boolean; s3loc: string };
    resumePdf: { ready: boolean; generated: boolean; s3loc: string };
    coverLetterHtml: { ready: boolean; generated: boolean; s3loc: string };
    coverLetterPdf: { ready: boolean; generated: boolean; s3loc: string };
  };
  allComplete: boolean;
}

// User
export interface User {
  userid: string;
  userhandle: string;
  email: string;
  username: string;
}

// Import Types
export interface ImportUrlResponse {
  uploadUrl: string;
  s3Key: string;
  expiresIn: number;
}

export interface ImportProcessResponse {
  resumejson: ResumeJSON;
  warnings?: string[];
  source: 'yaml' | 'json' | 'pdf';
}

// Organization Types
export interface Organization {
  org_id: string;
  org_name: string;
  billing_contact?: string;
  billing_email?: string;
  renewal_date?: string;
  member_count?: number;
  status?: 'active' | 'inactive' | 'suspended';
  created_at?: string;
  updated_at?: string;
}

export interface JoinOrganizationResponse {
  message: string;
  organization: {
    org_id: string;
    org_name: string;
  };
}
